import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, Alert, Typography, CircularProgress, IconButton, InputAdornment,
  Paper, Checkbox, FormControlLabel, RadioGroup, Radio, FormControl,
} from '@mui/material';
import { Visibility, VisibilityOff, AutoFixHigh, Refresh, ContentCopy, LockReset, Close as CloseIcon, UploadFile, DeleteOutline } from '@mui/icons-material';
import apiClient from '../../api/client';
import { Client } from '../../types';
import PhoneInput from '../common/PhoneInput';

interface ClientFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  client?: Client | null; // null = create mode
}

interface FormData {
  client_name: string;
  client_code: string;
  client_website: string;
  client_start_date: string;
  engagement_type: string;
  client_location: string;
  wh_timezone: string;
  wh_from: string;
  wh_to: string;
  primary_first_name: string;
  primary_last_name: string;
  primary_contact_number: string;
  primary_contact_email: string;
  secondary_first_name: string;
  secondary_last_name: string;
  secondary_contact_number: string;
  secondary_contact_email: string;
  notes: string;
  is_test: boolean;
  is_indirect: boolean;
  customer_username: string;
  customer_password: string;
}

const initialForm: FormData = {
  client_name: '',
  client_code: '',
  client_website: '',
  client_start_date: '',
  engagement_type: '',
  client_location: '',
  wh_timezone: 'EST',
  wh_from: '09:00',
  wh_to: '18:00',
  primary_first_name: '',
  primary_last_name: '',
  primary_contact_number: '',
  primary_contact_email: '',
  secondary_first_name: '',
  secondary_last_name: '',
  secondary_contact_number: '',
  secondary_contact_email: '',
  notes: '',
  is_test: false,
  is_indirect: false,
  customer_username: '',
  customer_password: '',
};

const ENGAGEMENT_TYPES = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'project-based', label: 'Project-based' },
];

const TIMEZONES = [
  'EST', 'CST', 'MST', 'PST', 'AST', 'HST', 'AKST',
  'UTC', 'GMT', 'CET', 'EET', 'IST', 'SGT', 'JST', 'CST (China)',
  'AEST', 'NZST', 'BRT', 'ART', 'SAST',
];

function generateTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

function generateCode(name: string): string {
  const letters = name.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const letterPart = letters.slice(0, 3).padEnd(3, 'X');
  const digitPart = String(Math.floor(Math.random() * 900) + 100);
  return letterPart + digitPart;
}

function generateUID(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]/g, '') || '';
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '@#$!';
  let pwd = '';
  for (let i = 0; i < 8; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  pwd += specials[Math.floor(Math.random() * specials.length)];
  return pwd;
}

/** Split full name into first/last by first space */
function splitName(fullName: string): { first: string; last: string } {
  const trimmed = (fullName || '').trim();
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1) };
}

/** Parse working hours string like "EST 09:00 - 18:00" */
function parseWorkingHours(wh: string | null): { tz: string; from: string; to: string } {
  if (!wh) return { tz: 'EST', from: '09:00', to: '18:00' };
  // Try format: "TZ HH:MM - HH:MM"
  const match = wh.match(/^(\S+)\s+(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
  if (match) {
    return {
      tz: match[1],
      from: match[2].padStart(5, '0'),
      to: match[3].padStart(5, '0'),
    };
  }
  // Fallback - return the string as-is in timezone, with defaults for time
  return { tz: 'EST', from: '09:00', to: '18:00' };
}

export default function ClientFormDialog({ open, onClose, onSaved, client }: ClientFormDialogProps) {
  const isEdit = !!client;
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Credentials dialog after create or password reset
  const [createdCreds, setCreatedCreds] = useState<{ username: string; password: string; isReset?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset password state (edit mode)
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordVal, setResetPasswordVal] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Change username state (edit mode)
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  const [changeUsernameVal, setChangeUsernameVal] = useState('');
  const [changingUsername, setChangingUsername] = useState(false);

  // Client logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setApiError('');
      setErrors({});
      setCopied(false);
      setCreatedCreds(null);
      setShowResetPassword(false);
      setResetPasswordVal('');
      setShowResetPwd(false);
      setShowChangeUsername(false);
      setChangeUsernameVal('');
      setLogoUrl((client as any)?.logo_url || null);

      if (client) {
        const primaryName = splitName(client.primary_contact_name);
        const secondaryName = splitName(client.secondary_contact_name || '');
        const wh = parseWorkingHours(client.preferred_working_hours);
        setForm({
          client_name: client.client_name,
          client_code: client.client_code,
          client_website: client.client_website || '',
          client_start_date: client.client_start_date?.slice(0, 10) || '',
          engagement_type: client.engagement_type || '',
          client_location: client.client_location || '',
          wh_timezone: wh.tz,
          wh_from: wh.from,
          wh_to: wh.to,
          primary_first_name: primaryName.first,
          primary_last_name: primaryName.last,
          primary_contact_number: client.primary_contact_number || '',
          primary_contact_email: client.primary_contact_email,
          secondary_first_name: secondaryName.first,
          secondary_last_name: secondaryName.last,
          secondary_contact_number: client.secondary_contact_number || '',
          secondary_contact_email: client.secondary_contact_email || '',
          notes: client.notes || '',
          is_test: client.is_test || false,
          is_indirect: client.is_indirect || false,
          customer_username: '',
          customer_password: '',
        });
      } else {
        setForm(initialForm);
      }
    }
  }, [open, client]);

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setForm(prev => {
      const updated = { ...prev, [field]: val };
      // Auto-generate code from name (only on create)
      if (field === 'client_name' && !isEdit) {
        updated.client_code = generateCode(val);
        updated.customer_username = generateUID(val);
      }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!form.client_name.trim()) errs.client_name = 'Client name is required';
    if (!form.client_code.trim()) errs.client_code = 'Client code is required';
    if (!form.primary_first_name.trim()) errs.primary_first_name = 'First name required';
    if (!form.primary_last_name.trim()) errs.primary_last_name = 'Last name required';
    if (!form.primary_contact_email.trim()) errs.primary_contact_email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.primary_contact_email)) errs.primary_contact_email = 'Invalid email';

    if (form.secondary_contact_email.trim() && !/\S+@\S+\.\S+/.test(form.secondary_contact_email))
      errs.secondary_contact_email = 'Invalid email';
    if (form.client_website.trim() && !/^https?:\/\/.+/i.test(form.client_website))
      errs.client_website = 'Must start with http:// or https://';

    // Customer login: if one field filled, both required
    if (!isEdit) {
      if (form.customer_username && !form.customer_password) errs.customer_password = 'Password required with username';
      if (form.customer_password && !form.customer_username) errs.customer_username = 'Username required with password';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError('');

    try {
      const primaryContactName = `${form.primary_first_name.trim()} ${form.primary_last_name.trim()}`.trim();
      const secondaryContactName = `${form.secondary_first_name.trim()} ${form.secondary_last_name.trim()}`.trim();
      const workingHours = `${form.wh_timezone} ${form.wh_from} - ${form.wh_to}`;

      const payload: any = {
        client_name: form.client_name.trim(),
        primary_contact_name: primaryContactName,
        primary_contact_number: form.primary_contact_number.trim() || null,
        primary_contact_email: form.primary_contact_email.trim(),
        secondary_contact_name: secondaryContactName || null,
        secondary_contact_number: form.secondary_contact_number.trim() || null,
        secondary_contact_email: form.secondary_contact_email.trim() || null,
        client_website: form.client_website.trim() || null,
        client_start_date: form.client_start_date || null,
        engagement_type: form.engagement_type || null,
        client_location: form.client_location.trim() || null,
        preferred_working_hours: workingHours,
        notes: form.notes.trim() || null,
        is_test: form.is_test,
        is_indirect: form.is_indirect,
      };

      if (logoUrl !== undefined) payload.logo_url = logoUrl;

      if (isEdit) {
        await apiClient.put(`/clients/${client!.id}`, payload);
        onSaved();
        onClose();
      } else {
        payload.client_code = form.client_code.trim().toUpperCase();
        if (form.customer_username && form.customer_password) {
          payload.customer_username = form.customer_username.trim();
          payload.customer_password = form.customer_password;
        }
        await apiClient.post('/clients', payload);

        // Show credentials if customer account was created
        if (form.customer_username && form.customer_password) {
          setCreatedCreds({ username: form.customer_username.trim(), password: form.customer_password });
        }

        onSaved();
        if (!form.customer_username || !form.customer_password) {
          onClose();
        }
      }
    } catch (err: any) {
      setApiError(err.response?.data?.error || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordVal.trim()) return;
    setResetting(true);
    setApiError('');

    try {
      await apiClient.post(`/clients/${client!.id}/reset-password`, { password: resetPasswordVal });
      setCreatedCreds({ username: client!.customer_username || '', password: resetPasswordVal, isReset: true });
      onSaved();
    } catch (err: any) {
      setApiError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleChangeUsername = async () => {
    if (!changeUsernameVal.trim()) return;
    setChangingUsername(true);
    setApiError('');

    try {
      await apiClient.post(`/clients/${client!.id}/change-username`, { username: changeUsernameVal.trim() });
      onSaved();
      setShowChangeUsername(false);
      setChangeUsernameVal('');
    } catch (err: any) {
      setApiError(err.response?.data?.error || 'Failed to change username');
    } finally {
      setChangingUsername(false);
    }
  };

  const handleCopyCredentials = () => {
    if (!createdCreds) return;
    navigator.clipboard.writeText(`Username: ${createdCreds.username}\nPassword: ${createdCreds.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sectionHeader = (label: string) => (
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1E293B', mt: 0.5 }}>
      {label}
    </Typography>
  );

  // Show credential dialog after create or password reset
  if (createdCreds) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {createdCreds.isReset ? 'Password Reset Successfully' : 'Client Created'}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Share these credentials with the customer. You can reset the password anytime from the edit dialog.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8' }}>Username</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem', mt: 0.3 }}>
              {createdCreds.username}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', mt: 2 }}>Password</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem', mt: 0.3 }}>
              {createdCreds.password}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            startIcon={<ContentCopy />}
            onClick={handleCopyCredentials}
            color={copied ? 'success' : 'primary'}
            sx={{ borderRadius: '10px' }}
          >
            {copied ? 'Copied!' : 'Copy Credentials'}
          </Button>
          <Button onClick={onClose} variant="contained" sx={{ borderRadius: '10px', px: 3 }}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ py: 1.5, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Client' : 'Add New Client'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, pb: 1, overflowX: 'hidden' }}>
        {apiError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px', py: 0 }}>{apiError}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* ── Client Type (top, prominent) ── */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl component="fieldset" sx={{ flex: 1 }}>
              <RadioGroup row value={form.is_indirect ? 'indirect' : 'direct'}
                onChange={(e) => setForm(prev => ({ ...prev, is_indirect: e.target.value === 'indirect' }))}>
                {[
                  { value: 'direct', label: 'Direct Client', color: '#1D4ED8', bg: form.is_indirect ? '#F8FAFC' : '#EFF6FF', border: form.is_indirect ? '#E2E8F0' : '#BFDBFE' },
                  { value: 'indirect', label: 'Indirect (Intermediary)', color: '#7E22CE', bg: !form.is_indirect ? '#F8FAFC' : '#F3E8FF', border: !form.is_indirect ? '#E2E8F0' : '#DDD6FE' },
                ].map(opt => (
                  <Box key={opt.value} onClick={() => setForm(prev => ({ ...prev, is_indirect: opt.value === 'indirect' }))}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '8px', border: `1.5px solid ${opt.border}`, backgroundColor: opt.bg, cursor: 'pointer', mr: 1, transition: 'all 0.15s' }}>
                    <Radio value={opt.value} size="small" sx={{ p: 0, color: opt.color, '&.Mui-checked': { color: opt.color } }} />
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: opt.color }}>{opt.label}</Typography>
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={form.is_test} onChange={(e) => setForm(prev => ({ ...prev, is_test: e.target.checked }))} size="small" color="warning" />}
              label={<Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#92400E' }}>Test client</Typography>}
              sx={{ mr: 0, px: 1.5, py: 0.75, borderRadius: '8px', border: '1.5px solid', borderColor: form.is_test ? '#FCD34D' : '#E2E8F0', backgroundColor: form.is_test ? '#FFFBEB' : '#F8FAFC', transition: 'all 0.15s' }}
            />
          </Box>

          {/* ── Client Information ── */}
          {sectionHeader('Client Information')}
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="Client Name" value={form.client_name} onChange={handleChange('client_name')} error={!!errors.client_name} helperText={errors.client_name} required size="small" autoFocus sx={{ flex: 2, minWidth: 0 }} />
            <TextField label="Client Code" value={form.client_code} onChange={handleChange('client_code')} error={!!errors.client_code} helperText={errors.client_code || (isEdit ? 'Cannot change code' : 'Auto-generated')} required size="small" disabled={isEdit} sx={{ flex: 1, minWidth: 0 }}
              InputProps={{ sx: { fontFamily: 'monospace', textTransform: 'uppercase' }, endAdornment: !isEdit ? (
                <InputAdornment position="end"><IconButton size="small" onClick={() => setForm(prev => ({ ...prev, client_code: generateCode(prev.client_name) }))} title="Regenerate code" edge="end"><Refresh sx={{ fontSize: 16 }} /></IconButton></InputAdornment>
              ) : undefined }}
            />
            <TextField label="Website" value={form.client_website} onChange={handleChange('client_website')} error={!!errors.client_website} helperText={errors.client_website} size="small" placeholder="https://example.com" sx={{ flex: 1.5, minWidth: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="Start Date" type="date" value={form.client_start_date} onChange={handleChange('client_start_date')} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 0 }} />
            <TextField select label="Engagement Type" value={form.engagement_type} onChange={handleChange('engagement_type')} size="small" sx={{ flex: 1, minWidth: 0 }}>
              <MenuItem value="">-- None --</MenuItem>
              {ENGAGEMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField label="Location" value={form.client_location} onChange={handleChange('client_location')} size="small" placeholder="Country, City" sx={{ flex: 1, minWidth: 0 }} />
          </Box>

          {/* ── Working Hours ── */}
          {sectionHeader('Working Hours')}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField select label="Timezone" value={form.wh_timezone} onChange={handleChange('wh_timezone')} size="small" sx={{ minWidth: 130 }}>
              {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
            </TextField>
            <TextField select label="From" value={form.wh_from} onChange={handleChange('wh_from')} size="small" sx={{ minWidth: 100 }}>
              {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Typography sx={{ color: '#94A3B8', fontSize: '0.8125rem' }}>to</Typography>
            <TextField select label="To" value={form.wh_to} onChange={handleChange('wh_to')} size="small" sx={{ minWidth: 100 }}>
              {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>

          {/* ── Primary Contact ── */}
          {sectionHeader('Primary Contact')}
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="First Name" value={form.primary_first_name} onChange={handleChange('primary_first_name')} error={!!errors.primary_first_name} helperText={errors.primary_first_name} required size="small" sx={{ flex: 1, minWidth: 0 }} />
            <TextField label="Last Name" value={form.primary_last_name} onChange={handleChange('primary_last_name')} error={!!errors.primary_last_name} helperText={errors.primary_last_name} required size="small" sx={{ flex: 1, minWidth: 0 }} />
            <TextField label="Email" type="email" value={form.primary_contact_email} onChange={handleChange('primary_contact_email')} error={!!errors.primary_contact_email} helperText={errors.primary_contact_email} required size="small" sx={{ flex: 1.5, minWidth: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
              <PhoneInput value={form.primary_contact_number} onChange={(val) => setForm(prev => ({ ...prev, primary_contact_number: val }))} />
            </Box>
          </Box>

          {/* ── Secondary Contact ── */}
          {sectionHeader('Secondary Contact (Optional)')}
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="First Name" value={form.secondary_first_name} onChange={handleChange('secondary_first_name')} size="small" sx={{ flex: 1, minWidth: 0 }} />
            <TextField label="Last Name" value={form.secondary_last_name} onChange={handleChange('secondary_last_name')} size="small" sx={{ flex: 1, minWidth: 0 }} />
            <TextField label="Email" type="email" value={form.secondary_contact_email} onChange={handleChange('secondary_contact_email')} error={!!errors.secondary_contact_email} helperText={errors.secondary_contact_email} size="small" sx={{ flex: 1.5, minWidth: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
              <PhoneInput value={form.secondary_contact_number} onChange={(val) => setForm(prev => ({ ...prev, secondary_contact_number: val }))} />
            </Box>
          </Box>

          {/* ── Notes ── */}
          {sectionHeader('Notes')}
          <TextField label="Notes" value={form.notes} onChange={handleChange('notes')} size="small" multiline rows={2} fullWidth />

          {/* ── Client Logo ── */}
          {sectionHeader('Client Logo (shown in customer portal)')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 80, height: 48, border: '1.5px dashed #CBD5E1', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#F8FAFC', overflow: 'hidden', flexShrink: 0,
            }}>
              {logoUrl
                ? <img src={logoUrl} alt="Client logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                : <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', textAlign: 'center' }}>No logo</Typography>
              }
            </Box>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<UploadFile sx={{ fontSize: 15 }} />}
              sx={{ borderRadius: '8px', fontSize: '0.75rem', borderColor: '#CBD5E1', color: '#475569' }}
            >
              Upload Logo
              <input type="file" accept="image/*" hidden onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => setLogoUrl(ev.target?.result as string);
                reader.readAsDataURL(file);
                e.target.value = '';
              }} />
            </Button>
            {logoUrl && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutline sx={{ fontSize: 15 }} />}
                onClick={() => setLogoUrl(null)}
                sx={{ borderRadius: '8px', fontSize: '0.75rem' }}
              >
                Remove
              </Button>
            )}
          </Box>

          {/* ── Customer Portal Login ── */}
          {sectionHeader(`Customer Portal${!isEdit ? ' (Optional)' : ''}`)}
          {!isEdit ? (
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
              <TextField label="Username" value={form.customer_username} onChange={handleChange('customer_username')} error={!!errors.customer_username} helperText={errors.customer_username || 'Auto-generated'} size="small" autoComplete="off" name="customer_username_new"
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              <TextField label="Password" type={showPassword ? 'text' : 'password'} value={form.customer_password} onChange={handleChange('customer_password')} error={!!errors.customer_password} helperText={errors.customer_password} size="small" autoComplete="new-password" name="customer_password_new"
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton>
                    <IconButton size="small" onClick={() => { setForm(prev => ({ ...prev, customer_password: generatePassword() })); setShowPassword(true); }} title="Auto-generate" edge="end"><AutoFixHigh sx={{ fontSize: 16 }} /></IconButton>
                  </InputAdornment>
                ) }}
              />
            </Box>
          ) : (
            <Box sx={{ backgroundColor: '#FFFBEB', borderRadius: '8px', border: '1px solid #FEF3C7', overflow: 'hidden' }}>
              {client?.customer_username ? (
                <>
                  {/* Username row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, borderBottom: '1px solid #FEF3C7' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em', width: 72, flexShrink: 0 }}>Username</Typography>
                    {showChangeUsername ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
                        <TextField value={changeUsernameVal} onChange={(e) => setChangeUsernameVal(e.target.value.toLowerCase())} size="small" autoFocus
                          sx={{ flex: 1 }} InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                        />
                        <Button variant="contained" color="info" size="small" onClick={handleChangeUsername} disabled={changingUsername || !changeUsernameVal.trim()} sx={{ whiteSpace: 'nowrap', minWidth: 56, height: 36, borderRadius: '6px', fontSize: '0.75rem' }}>
                          {changingUsername ? <CircularProgress size={14} /> : 'Save'}
                        </Button>
                        <Button size="small" color="inherit" onClick={() => { setShowChangeUsername(false); setChangeUsernameVal(''); }} sx={{ height: 36, fontSize: '0.75rem' }}>Cancel</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.875rem', color: '#1E293B', flex: 1 }}>{client.customer_username}</Typography>
                        <Button size="small" color="info" onClick={() => { setShowChangeUsername(true); setChangeUsernameVal(client?.customer_username || ''); }} sx={{ borderRadius: '6px', fontSize: '0.75rem', ml: 1 }}>
                          Change Username
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {/* Password row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em', width: 72, flexShrink: 0 }}>Password</Typography>
                    {showResetPassword ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
                        <TextField type={showResetPwd ? 'text' : 'password'} value={resetPasswordVal} onChange={(e) => setResetPasswordVal(e.target.value)} size="small" autoFocus
                          sx={{ flex: 1 }}
                          InputProps={{ endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowResetPwd(!showResetPwd)} edge="end">{showResetPwd ? <VisibilityOff sx={{ fontSize: 15 }} /> : <Visibility sx={{ fontSize: 15 }} />}</IconButton>
                              <IconButton size="small" onClick={() => { setResetPasswordVal(generatePassword()); setShowResetPwd(true); }} edge="end"><AutoFixHigh sx={{ fontSize: 15 }} /></IconButton>
                            </InputAdornment>
                          ) }}
                        />
                        <Button variant="contained" color="warning" size="small" onClick={handleResetPassword} disabled={resetting || !resetPasswordVal.trim()} sx={{ whiteSpace: 'nowrap', minWidth: 56, height: 36, borderRadius: '6px', fontSize: '0.75rem' }}>
                          {resetting ? <CircularProgress size={14} /> : 'Reset'}
                        </Button>
                        <Button size="small" color="inherit" onClick={() => { setShowResetPassword(false); setResetPasswordVal(''); }} sx={{ height: 36, fontSize: '0.75rem' }}>Cancel</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.875rem', color: '#1E293B', flex: 1 }}>
                          {client?.customer_plain_password || '••••••••'}
                        </Typography>
                        <Button startIcon={<LockReset sx={{ fontSize: 15 }} />} size="small" color="warning" onClick={() => { setShowResetPassword(true); setResetPasswordVal(generatePassword()); setShowResetPwd(true); }} sx={{ borderRadius: '6px', fontSize: '0.75rem', ml: 1 }}>
                          Reset Password
                        </Button>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 1.5, py: 1 }}>No customer account created for this client.</Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
        <Button onClick={onClose} color="inherit" disabled={saving} variant="outlined" sx={{ borderRadius: '10px', px: 3, borderColor: '#E2E8F0', color: '#64748B' }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ borderRadius: '10px', px: 3 }}>
          {saving ? <CircularProgress size={18} /> : isEdit ? 'Save Changes' : 'Create Client'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
